import logger from '@/lib/logger';
import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { toPng } from 'html-to-image';
import { X, DownloadSimple, ShareNetwork, Fire, Timer, Barbell, Trophy, Lightning, Copy, Check } from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ShareStreakModal({ isOpen, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [cardStyle, setCardStyle] = useState('fire'); // fire, minimal, neon
  const cardRef = useRef(null);

  const fetchShareData = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/streak/share-data`);
      setData(data);
    } catch (err) {
      logger.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      fetchShareData();
    }
  }, [isOpen, fetchShareData]);

  const downloadCard = async () => {
    if (!cardRef.current) return;
    setDownloading(true);
    try {
      const dataUrl = await toPng(cardRef.current, {
        quality: 1.0,
        pixelRatio: 2,
        backgroundColor: '#0A0A0A',
      });
      const link = document.createElement('a');
      link.download = `nogear-streak-${data?.streak || 0}-days.png`;
      link.href = dataUrl;
      link.click();
    } catch (err) {
      logger.error('Download failed:', err);
    } finally {
      setDownloading(false);
    }
  };

  const shareCard = async () => {
    if (!cardRef.current) return;
    try {
      const dataUrl = await toPng(cardRef.current, {
        quality: 1.0,
        pixelRatio: 2,
        backgroundColor: '#0A0A0A',
      });
      
      // Try native share API
      if (navigator.share && navigator.canShare) {
        const blob = await (await fetch(dataUrl)).blob();
        const file = new File([blob], 'nogear-streak.png', { type: 'image/png' });
        if (navigator.canShare({ files: [file] })) {
          await navigator.share({
            title: `${data?.streak} Day Streak on GRIT.FIT!`,
            text: `I'm on a ${data?.streak}-day workout streak! ${data?.quote}`,
            files: [file],
          });
          return;
        }
      }
      
      // Fallback: copy text to clipboard
      const text = `I'm on a ${data?.streak}-day workout streak with ${data?.total_workouts} total workouts on GRIT.FIT! ${data?.quote} #GritFit #HomeWorkout #FitnessStreak`;
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      logger.error('Share failed:', err);
    }
  };

  if (!isOpen) return null;

  const CARD_STYLES = {
    fire: {
      bg: 'bg-gradient-to-br from-[#0A0A0A] via-[#1a0505] to-[#0A0A0A]',
      accent: '#FF3B30',
      accentGlow: 'shadow-[0_0_60px_rgba(255,59,48,0.3)]',
      pattern: (
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 right-0 w-64 h-64 bg-[#FF3B30] rounded-full blur-[120px]" />
          <div className="absolute bottom-0 left-0 w-48 h-48 bg-[#FF3B30] rounded-full blur-[100px]" />
        </div>
      ),
    },
    minimal: {
      bg: 'bg-[#0A0A0A]',
      accent: '#FFFFFF',
      accentGlow: '',
      pattern: (
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{ backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 20px, rgba(255,255,255,0.03) 20px, rgba(255,255,255,0.03) 21px)' }} />
        </div>
      ),
    },
    neon: {
      bg: 'bg-gradient-to-br from-[#0A0A0A] via-[#050a14] to-[#0A0A0A]',
      accent: '#007AFF',
      accentGlow: 'shadow-[0_0_60px_rgba(0,122,255,0.3)]',
      pattern: (
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-1/2 w-72 h-72 bg-[#007AFF] rounded-full blur-[140px] -translate-x-1/2" />
          <div className="absolute bottom-10 right-10 w-40 h-40 bg-[#34C759] rounded-full blur-[80px]" />
        </div>
      ),
    },
  };

  const style = CARD_STYLES[cardStyle];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="share-streak-modal">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={onClose} />
      
      {/* Modal */}
      <div className="relative z-10 w-full max-w-lg bg-[#141414] border border-white/10">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-white/10">
          <h3 className="text-xl font-bold tracking-tight font-['Barlow_Condensed'] uppercase">Share Your Streak</h3>
          <button onClick={onClose} className="text-[#71717A] hover:text-white transition-colors" data-testid="close-share-modal">
            <X weight="bold" className="w-5 h-5" />
          </button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-8 h-8 border-2 border-[#FF3B30] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <div className="p-5">
            {/* Style Selector */}
            <div className="flex gap-2 mb-4">
              <span className="label-tactical self-center mr-2">Style</span>
              {['fire', 'minimal', 'neon'].map(s => (
                <button
                  key={s}
                  onClick={() => setCardStyle(s)}
                  data-testid={`card-style-${s}`}
                  className={`px-4 py-1.5 text-xs font-bold uppercase tracking-wider transition-all ${cardStyle === s ? 'bg-[#FF3B30] text-white' : 'bg-[#1C1C1E] border border-white/10 text-[#A1A1AA] hover:border-white/20'}`}
                >
                  {s}
                </button>
              ))}
            </div>

            {/* Card Preview */}
            <div className="flex justify-center mb-5">
              <div
                ref={cardRef}
                className={`relative w-[400px] aspect-square ${style.bg} overflow-hidden`}
                style={{ fontFamily: "'Barlow Condensed', 'DM Sans', sans-serif" }}
                data-testid="share-card"
              >
                {style.pattern}
                
                {/* Card Content */}
                <div className="relative z-10 h-full flex flex-col justify-between p-8">
                  {/* Top - Brand */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 flex items-center justify-center" style={{ background: `${style.accent}20`, border: `1px solid ${style.accent}40` }}>
                        <Lightning weight="fill" className="w-4 h-4" style={{ color: style.accent }} />
                      </div>
                      <span className="text-sm font-black uppercase tracking-tight" style={{ color: style.accent }}>GRIT.FIT</span>
                    </div>
                    <span className="text-xs uppercase tracking-[0.2em] text-[#71717A] font-bold">
                      {data?.date}
                    </span>
                  </div>

                  {/* Center - Streak */}
                  <div className="text-center">
                    <div className="flex justify-center mb-2">
                      <Fire weight="fill" className="w-12 h-12" style={{ color: style.accent }} />
                    </div>
                    <div className="text-8xl font-black leading-none" style={{ color: style.accent, textShadow: `0 0 40px ${style.accent}40` }}>
                      {data?.streak || 0}
                    </div>
                    <div className="text-xl font-bold uppercase tracking-[0.3em] text-white/80 mt-2">
                      Day Streak
                    </div>
                  </div>

                  {/* Stats Row */}
                  <div>
                    <div className="grid grid-cols-3 gap-3 mb-5">
                      <div className="text-center">
                        <Barbell weight="bold" className="w-5 h-5 mx-auto mb-1" style={{ color: style.accent }} />
                        <div className="text-2xl font-black text-white">{data?.total_workouts || 0}</div>
                        <div className="text-[10px] uppercase tracking-[0.15em] text-[#71717A]">Workouts</div>
                      </div>
                      <div className="text-center">
                        <Timer weight="bold" className="w-5 h-5 mx-auto mb-1" style={{ color: style.accent }} />
                        <div className="text-2xl font-black text-white">{data?.total_minutes || 0}</div>
                        <div className="text-[10px] uppercase tracking-[0.15em] text-[#71717A]">Minutes</div>
                      </div>
                      <div className="text-center">
                        <Trophy weight="bold" className="w-5 h-5 mx-auto mb-1" style={{ color: style.accent }} />
                        <div className="text-2xl font-black text-white">{data?.longest_streak || 0}</div>
                        <div className="text-[10px] uppercase tracking-[0.15em] text-[#71717A]">Best Streak</div>
                      </div>
                    </div>

                    {/* Quote */}
                    <div className="border-t border-white/10 pt-4">
                      <p className="text-xs text-[#A1A1AA] italic text-center leading-relaxed">
                        "{data?.quote}"
                      </p>
                      <p className="text-[10px] text-[#71717A] text-center mt-2 uppercase tracking-[0.15em]">
                        {data?.name} | {data?.fitness_level}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Border glow effect */}
                <div className={`absolute inset-0 border ${cardStyle === 'minimal' ? 'border-white/10' : ''}`} style={{ borderColor: cardStyle !== 'minimal' ? `${style.accent}30` : undefined }} />
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3">
              <button
                onClick={downloadCard}
                disabled={downloading}
                className="btn-primary flex-1 py-3 flex items-center justify-center gap-2"
                data-testid="download-streak-card"
              >
                <DownloadSimple weight="bold" className="w-5 h-5" />
                {downloading ? 'Generating...' : 'Download Image'}
              </button>
              <button
                onClick={shareCard}
                className="btn-secondary flex-1 py-3 flex items-center justify-center gap-2"
                data-testid="share-streak-button"
              >
                {copied ? (
                  <><Check weight="bold" className="w-5 h-5 text-[#34C759]" /> Copied!</>
                ) : (
                  <><ShareNetwork weight="bold" className="w-5 h-5" /> Share</>
                )}
              </button>
            </div>
            <p className="text-xs text-[#71717A] text-center mt-3">
              Download the card and share it on Instagram, Twitter, or any social platform.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
